/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from '@msdyn365-commerce/core';
import { IModuleProps, INodeProps } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';

import { IImageListConfig, IImageListProps, IImagesData } from './image-list.props.autogenerated';

/**
 * The interface for IImageListViewProps.
 */
export interface IImageListViewProps extends IImageListProps<IImageListConfig> {
    imageList: IModuleProps;
    imageListHeading?: React.ReactNode;
    imageListContainer: INodeProps;
    imageListItem: INodeProps;
    imageListItemAnchorTag: INodeProps;
    imageListItems: IImageListItem[] | undefined;
}

/**
 * The interface for IImageListItem.
 */
export interface IImageListItem {
    image: JSX.Element | undefined;
    text: JSX.Element | undefined;
    link: string | undefined;
    linkAltText: string | undefined;
    linkAriaLabel: string | undefined;
}

/**
 *
 * ImageList component.
 * @extends {React.PureComponent<IImageListProps<IImageListConfig>>}
 */
class ImageList extends React.PureComponent<IImageListProps<IImageListConfig>> {
    public render(): JSX.Element | null {
        const { heading } = this.props.config;
        const imageListTitle = heading && <Msdyn365.Text className='ms-image-list__title' tag={heading.tag ?? 'h1'} text={heading.text} />;
        const viewProps: IImageListViewProps = {
            ...this.props,
            imageList: {
                moduleProps: this.props,
                className: classnames('ms-image-list', this.props.config.className),
                tag: 'div'
            },
            imageListHeading: imageListTitle,
            imageListContainer: {
                className: 'ms-image-list__list',
                role: 'list',
                tag: 'ul'
            },
            imageListItem: {
                className: 'ms-image-list__list-item',
                role: 'listitem',
                tag: 'li'
            },
            imageListItemAnchorTag: {
                tag: 'a',
                className: 'ms-image-list__link',
                role: 'link'
            },
            imageListItems: this._renderImageListItems()
        };
        return this.props.renderView(viewProps);
    }

    /**
     * Handle paragraph change.
     * @param index - Index of image item in array of image items.
     * @returns Void.
     */
    public handleParagraphChange = (index: number) => (event: Msdyn365.ContentEditableEvent): void => {
        if (this.props.config.images![index]) {
            this.props.config.images![index].paragraph = event.target.value;
        }
    };

    /**
     * Render list of images.
     * @returns List of images with text and redirection link.
     */
    private readonly _renderImageListItems = (): IImageListItem[] | undefined => {
        const imageList: IImagesData[] | undefined = this.props.config.images;
        const defaultImageSettings: Msdyn365.IImageSettings = {
            viewports: {
                xs: { q: 'w=65&h=65&q=80&m=6', w: 65, h: 65 },
                lg: { q: 'w=65&h=65&q=80&m=6', w: 65, h: 65 },
                xl: { q: 'w=65&h=65&q=80&m=6', w: 65, h: 65 }
            },
            lazyload: false,
            disableLazyLoad: true
        };
        return imageList?.map((item, index) => {
            const { image, link, paragraph } = item;

            const imageProps = {
                gridSettings: this.props.context.request.gridSettings!,
                imageSettings: (image?.imageSettings ?? defaultImageSettings) as Msdyn365.IImageSettings | undefined
            };

            const imageLink: string | undefined = link?.linkUrl.destinationUrl;
            const imageLinkText: string | undefined = link?.linkText ?? link?.ariaLabel;
            const imageLinkAriaLabel: string | undefined = link?.ariaLabel;

            // If image is null, we want to render the placeholder SVG - by passing image data with empty src
            const imageImage = (
                <Msdyn365.Image {...image} {...imageProps} loadFailureBehavior='empty' requestContext={this.props.context.request} />
            );

            const imageText = paragraph && (
                <Msdyn365.RichTextComponent
                    text={paragraph as Msdyn365.RichText | undefined}
                    className='ms-image-list__text'
                    editProps={{ onEdit: this.handleParagraphChange(index), requestContext: this.props.context.request }}
                />
            );
            const imageListItem: IImageListItem = {
                image: imageImage,
                text: imageText,
                link: imageLink,
                linkAltText: imageLinkText,
                linkAriaLabel: imageLinkAriaLabel
            };
            return imageListItem;
        });
    };
}

export default ImageList;
